/**
 * Sign in (T2.11).
 *
 * The form posts to /auth/login and that is the whole of it: the API sets the
 * httpOnly cookies, the browser stores nothing, and this component never sees a
 * token. Do not "improve" that by keeping anything in localStorage — the cookie
 * design exists so an XSS cannot exfiltrate a session (design.md § 3).
 *
 * Failure messages stay deliberately vague. "No account with that email" tells
 * an attacker which addresses are registered; a single message for both wrong
 * email and wrong password does not.
 */
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { useTerms } from '../lib/terms';
import { useDocumentTitle } from '../lib/documentTitle';
import { ApiError } from '../lib/api';

interface LocationState {
  from?: string;
}

export function LoginRoute() {
  const terms = useTerms();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useDocumentTitle('Sign in', terms.plural('blueprint'));

  // Where the user was heading before the guard intercepted them.
  const from = (location.state as LocationState | null)?.from ?? '/blueprints';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await signIn({ email: email.trim(), password });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Wait a few minutes and try again.');
      } else if (err instanceof ApiError && err.status === 401) {
        // One message for both causes — see the note at the top of this file.
        setError('That email address and password do not match an active account.');
      } else {
        setError('Could not sign you in. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet login-sheet">
      <div className="masthead">
        <div>
          <h1>Sign in</h1>
          <p className="meta">
            Sign in to view a {terms.noun('blueprint')} you have been given access to.
          </p>
        </div>
      </div>

      <form className="login-form" onSubmit={onSubmit} noValidate>
        <label className="field">
          <span className="field-label">Email address</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">Password</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {/* role="alert" so a screen reader announces the failure without the
            user having to go looking for it. */}
        {error ? (
          <p className="status err" role="alert">
            {error}
          </p>
        ) : null}

        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
